import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { createPresence, startPause, editPresenceDetails, fetchPresences } from '../../../redux/slices/presenceSlice';
import { updatePauseTimes } from '../../../redux/slices/pausesSlice';

const WORK_LOCATIONS = [
    { value: "Work", label: "Work" },
    { value: "Meeting", label: "Meeting" }
];

const PAUSE_REASONS = [
    { value: "Almoço", label: "Almoço" },
    { value: "Interrupção de Trabalho", label: "Interrupção de Trabalho" }
];

const AddPresenceManual = ({ show, onHide, refreshPresences }) => {
    const dispatch = useDispatch();

    const [checkInDateTime, setCheckInDateTime] = useState('');
    const [checkOutDateTime, setCheckOutDateTime] = useState('');
    const [workLocation, setWorkLocation] = useState('Work');
    const [expectedHours, setExpectedHours] = useState(8);
    const [pauses, setPauses] = useState([]);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const addPause = () => {
        setPauses(prev => [...prev, { start: '', end: '', reason: 'Almoço' }]);
    };

    const updatePause = (index, field, value) => {
        setPauses(prev => prev.map((pause, i) => i === index ? { ...pause, [field]: value } : pause));
    };

    const removePause = (index) => {
        setPauses(prev => prev.filter((_, i) => i !== index));
    };

    const validatePauses = () => {
        for (let i = 0; i < pauses.length; i++) {
            const p = pauses[i];
            if (!p.start || !p.end) {
                setError(`Pausa ${i + 1}: Início e fim são obrigatórios.`);
                return false;
            }
            if (new Date(p.end) <= new Date(p.start)) {
                setError(`Pausa ${i + 1}: Fim deve ser depois do início.`);
                return false;
            }
            if (new Date(p.start) < new Date(checkInDateTime)) {
                setError(`Pausa ${i + 1}: Início não pode ser antes do check-in.`);
                return false;
            }
            if (new Date(p.end) > new Date(checkOutDateTime)) {
                setError(`Pausa ${i + 1}: Fim não pode ser depois do check-out.`);
                return false;
            }
        }
        return true;
    };

    const handleSave = async () => {
        if (!checkInDateTime || !checkOutDateTime) {
            setError('Check-in e Check-out são obrigatórios.');
            return;
        }
        if (new Date(checkOutDateTime) <= new Date(checkInDateTime)) {
            setError('Check-out deve ser depois do Check-in.');
            return;
        }
        if (!expectedHours || expectedHours <= 0) {
            setError('Expected hours deve ser um número positivo.');
            return;
        }
        if (!validatePauses()) {
            return;
        }
        setError('');
        setSaving(true);
        try {
            const presence = await dispatch(createPresence({
                check_in: new Date(checkInDateTime).toISOString(),
                type: workLocation,
                expected_hours: expectedHours
            })).unwrap();

            await dispatch(editPresenceDetails({
                presenceId: presence.id,
                updates: {
                    check_out: new Date(checkOutDateTime).toISOString()
                }
            })).unwrap();

            for (const pause of pauses) {
                const startedPause = await dispatch(startPause({
                    presenceId: presence.id.toString(),
                    reason: pause.reason,
                    start_location: "office",
                    start: pause.start ? new Date(pause.start).toISOString() : undefined
                })).unwrap();

            const pauseId = typeof startedPause.id === 'object' && startedPause.id !== null ? startedPause.id : startedPause.id;
            await dispatch(updatePauseTimes({
                pauseId: pauseId,
                timeData: {
                    start: pause.start ? new Date(pause.start).toISOString() : null,
                    end: pause.end ? new Date(pause.end).toISOString() : null,
                    reason: pause.reason || ""
                }
            })).unwrap();
            }

            await dispatch(fetchPresences());
            if (refreshPresences) refreshPresences();
            onHide();
        } catch (e) {
            setError('Falha ao adicionar presença. Por favor, tente novamente.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Adicionar Presença Manualmente</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form>
                    <Form.Group className="mb-3" controlId="checkInDateTime">
                        <Form.Label>Check-In</Form.Label>
                        <Form.Control
                            type="datetime-local"
                            value={checkInDateTime}
                            onChange={e => setCheckInDateTime(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="checkOutDateTime">
                        <Form.Label>Check-Out</Form.Label>
                        <Form.Control
                            type="datetime-local"
                            value={checkOutDateTime}
                            onChange={e => setCheckOutDateTime(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="workLocation">
                        <Form.Label>Work Location</Form.Label>
                        <Form.Select
                            value={workLocation}
                            onChange={e => setWorkLocation(e.target.value)}
                        >
                            {WORK_LOCATIONS.map(loc => (
                                <option key={loc.value} value={loc.value}>{loc.label}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="expectedHours" style={{ display: 'none' }}>
                        <Form.Label>Expected Hours</Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            step="0.1"
                            value={expectedHours}
                            onChange={e => setExpectedHours(parseFloat(e.target.value))}
                        />
                    </Form.Group>

                    <hr />
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5>Pausas</h5>
                        <Button variant="secondary" size="sm" onClick={addPause}>Adicionar Pausa</Button>
                    </div>
                    {pauses.length === 0 && <div className="mb-3">Nenhuma pausa adicionada.</div>}
                    {pauses.map((pause, idx) => (
                        <div key={idx} className="border rounded p-3 mb-3">
                            <Row>
                                <Col md={5}>
                                    <Form.Group controlId={`pauseStart${idx}`}>
                                        <Form.Label>Início da Pausa</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={pause.start}
                                            onChange={e => updatePause(idx, 'start', e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={5}>
                                    <Form.Group controlId={`pauseEnd${idx}`}>
                                        <Form.Label>Fim da Pausa</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={pause.end}
                                            onChange={e => updatePause(idx, 'end', e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Form.Group controlId={`pauseReason${idx}`}>
                                        <Form.Label>Motivo</Form.Label>
                                        <Form.Select
                                            value={pause.reason}
                                            onChange={e => updatePause(idx, 'reason', e.target.value)}
                                        >
                                            {PAUSE_REASONS.map(reason => (
                                                <option key={reason.value} value={reason.value}>{reason.label}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <div className="text-end mt-2">
                                <Button variant="danger" size="sm" onClick={() => removePause(idx)}>Remover Pausa</Button>
                            </div>
                        </div>
                    ))}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                    {saving ? 'A Salvar...' : 'Salvar'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddPresenceManual;
